import { useState, useEffect, useCallback, useRef } from "react";
import {
  ItemOperation,
  OperationQuantityBreak,
  convertRunTimeValue,
  DEFAULT_ITEM_ADVANCED_SETTINGS,
  getRunTimeHours,
  resolveRunTimeFormat,
  RunTimeFormat,
  RunTimeFormatSchema,
  SEQUENCE_NUMBER_MAX_LENGTH,
  UNIT_COST_CALCULATION,
} from "@forgepoint/shared";
import { useOperationsStore } from "@/modules/operations/operationsStore";
import OperationEditorSheet from "@/modules/operations/OperationEditorSheet";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Edit2, Loader2, Trash2, Plus, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { OperationSelector } from "@/components/OperationSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Decimal from "decimal.js";
import { parseDecimal } from "./utils";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";

const RUN_TIME_FORMAT_LABEL: Record<RunTimeFormat, string> = {
  PIECES_PER_HOUR: "Pieces/hour",
  HOURS_PER_PIECE: "Hours/piece",
  MINUTES_PER_PIECE: "Minutes/piece",
};

interface BOMOperationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "add" | "edit";
  operation?: ItemOperation;
  onSave: (operation: ItemOperation) => Promise<boolean | void>;
  onRemove?: () => void;
  onUpdated?: () => void;
  /** Sequence number pre-filled in add mode, e.g. the next multiple of ten. */
  suggestedSequenceNumber?: string;
  /** Freezes the fields this BOM owns; the operation record stays editable. */
  locked?: boolean;
  lockReason?: string;
}

/**
 * Unified sheet component for adding or editing BOM operations.
 * Handles both creating new item operations and modifying existing ones, and
 * hides the efficiency, quantity per, and quantity break fields the org's item
 * settings disallow.
 * @param props - The operation, sheet mode, and save handlers
 * @returns The BOM operation sheet
 */
export default function BOMOperationSheet({
  open,
  onOpenChange,
  mode,
  operation,
  onSave,
  onRemove,
  onUpdated,
  suggestedSequenceNumber = "",
  locked = false,
  lockReason,
}: BOMOperationSheetProps) {
  const { operations, fetchOperations } = useOperationsStore();
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const sequenceNumbersEnabled = useOrganizationStore(
    (state) => state.organization?.bomSequenceNumbersEnabled === true,
  );
  const itemSettings =
    useItemSettingsStore((state) => state.settings) ??
    DEFAULT_ITEM_ADVANCED_SETTINGS;
  const efficiencyAllowed = itemSettings.allowOperationEfficiency;
  const qtyPerAllowed = itemSettings.allowOperationQtyPer;
  const quantityBreaksAllowed = itemSettings.allowOperationQuantityBreaks;

  const [sequenceNumber, setSequenceNumber] = useState("");
  const [selectedOperationId, setSelectedOperationId] = useState("");
  const [setupTime, setSetupTime] = useState("0");
  const [runTimePerItem, setRunTimePerItem] = useState("");
  const [quantityPer, setQuantityPer] = useState("1");
  const [runTimeFormat, setRunTimeFormat] =
    useState<RunTimeFormat>("HOURS_PER_PIECE");
  const [efficiency, setEfficiency] = useState("100");
  const [overrideOperationCost, setOverrideOperationCost] = useState("");
  const [overrideSetupRate, setOverrideSetupRate] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOperationEditorOpen, setIsOperationEditorOpen] = useState(false);
  const [minimumCharge, setMinimumCharge] = useState("");
  const [showCostOverrides, setShowCostOverrides] = useState(false);
  const [quantityBreaks, setQuantityBreaks] = useState<
    OperationQuantityBreak[]
  >([]);
  const hasInitializedRef = useRef(false);

  const isEditMode = mode === "edit";
  const selectedOperation = selectedOperationId
    ? operations.get(selectedOperationId)
    : null;

  useEffect(() => {
    if (open && operations.size === 0) {
      fetchOperations();
    }
  }, [open, operations.size, fetchOperations]);

  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    // Parent refreshes must not overwrite the open sheet's unsaved edits.
    if (hasInitializedRef.current || (isEditMode && !operation)) {
      return;
    }
    if (open && !isSubmitting) {
      hasInitializedRef.current = true;
      if (isEditMode && operation) {
        const nextQuantityPer = operation.qtyPer ?? "1";
        const nextRunTimeFormat = resolveRunTimeFormat(operation.runTimeFormat);
        setSequenceNumber(operation.sequenceNumber ?? "");
        setSelectedOperationId(operation.operationId);
        setSetupTime(operation.setupTime || "");
        setRunTimePerItem(operation.runTimePerItem || "");
        setQuantityPer(nextQuantityPer);
        setRunTimeFormat(nextRunTimeFormat);
        setEfficiency(operation.efficiency || "100");
        setOverrideOperationCost(operation.overrideOperationCost || "");
        setOverrideSetupRate(operation.overrideSetupRate || "");
        setReasoning(operation.reasoning || "");
        setMinimumCharge(operation.minimumCharge || "");
        setShowCostOverrides(hasCostOverride(operation));
        setQuantityBreaks(operation.quantityBreaks ?? []);
      } else {
        setSequenceNumber("");
        setSelectedOperationId("");
        setSetupTime("0");
        setRunTimePerItem("");
        setQuantityPer("1");
        setRunTimeFormat("HOURS_PER_PIECE");
        setEfficiency("100");
        setOverrideOperationCost("");
        setOverrideSetupRate("");
        setReasoning("");
        setMinimumCharge("");
        setShowCostOverrides(false);
        setQuantityBreaks([]);
      }
    }
  }, [open, isEditMode, operation, isSubmitting]);

  // Seeded outside the reset effect so a late organization load cannot wipe the form.
  useEffect(() => {
    if (!open || isSubmitting || isEditMode || !sequenceNumbersEnabled) {
      return;
    }
    setSequenceNumber((current) => current || suggestedSequenceNumber);
  }, [
    open,
    isSubmitting,
    isEditMode,
    sequenceNumbersEnabled,
    suggestedSequenceNumber,
  ]);

  const handleSave = async () => {
    if (!selectedOperationId) {
      toast.error("Please select an operation");
      return;
    }
    const parsedQuantityPer = parseDecimal(quantityPer);
    if (!parsedQuantityPer || parsedQuantityPer.lte(0)) {
      toast.error("Please enter a production quantity greater than zero");
      return;
    }

    if (!isPerUnit) {
      if (!setupTime || !runTimePerItem) {
        toast.error("Please enter setup time and runtime");
        return;
      }
      if (!efficiency || parseFloat(efficiency) <= 0) {
        toast.error("Please enter a valid efficiency (>=1%)");
        return;
      }
    }

    const runTimeHours = runTimePerItem
      ? getRunTimeHours(runTimePerItem, runTimeFormat, quantityPer)
      : null;
    if (!isPerUnit && runTimeHours === null) {
      toast.error("Please enter a valid runtime");
      return;
    }

    const hasChanges =
      !isEditMode ||
      !operation ||
      selectedOperationId !== operation.operationId ||
      sequenceNumber !== (operation.sequenceNumber || "") ||
      setupTime !== (operation.setupTime || "") ||
      runTimePerItem !== (operation.runTimePerItem || "") ||
      quantityPer !== (operation.qtyPer || "1") ||
      runTimeFormat !== resolveRunTimeFormat(operation.runTimeFormat) ||
      (!isPerUnit && efficiency !== (operation.efficiency || "100")) ||
      reasoning !== (operation.reasoning || "") ||
      overrideOperationCost !== (operation.overrideOperationCost || "") ||
      overrideSetupRate !== (operation.overrideSetupRate || "") ||
      minimumCharge !== (operation.minimumCharge || "") ||
      JSON.stringify(quantityBreaks) !==
        JSON.stringify(operation.quantityBreaks ?? []);

    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const sortedBreaks = [...quantityBreaks].sort(
        (a, b) => a.minQty - b.minQty,
      );
      const breaksToSave = sortedBreaks.length > 0 ? sortedBreaks : undefined;

      const trimmedSequenceNumber = sequenceNumber.trim();

      const itemOperation: ItemOperation = isPerUnit
        ? {
            id: operation?.id,
            operationId: selectedOperationId,
            sequenceNumber: trimmedSequenceNumber || null,
            ...(setupTime && { setupTime }),
            ...(runTimePerItem && { runTimePerItem }),
            qtyPer: quantityPer,
            ...(operation?.runTimeFormat && {
              runTimeFormat: operation.runTimeFormat,
            }),
            efficiency: "100",
            reasoning,
            description: operations.get(selectedOperationId)?.name,
            ...(overrideOperationCost && { overrideOperationCost }),
            ...(minimumCharge && { minimumCharge }),
            ...(breaksToSave && { quantityBreaks: breaksToSave }),
          }
        : {
            id: operation?.id,
            operationId: selectedOperationId,
            sequenceNumber: trimmedSequenceNumber || null,
            setupTime,
            runTimePerItem,
            qtyPer: quantityPer,
            runTimeFormat,
            efficiency,
            reasoning,
            description: operations.get(selectedOperationId)?.name,
            ...(overrideOperationCost && { overrideOperationCost }),
            ...(overrideSetupRate && { overrideSetupRate }),
            ...(minimumCharge && { minimumCharge }),
            ...(breaksToSave && { quantityBreaks: breaksToSave }),
          };

      const result = await onSave(itemOperation);

      if (result !== false) {
        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!onRemove) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onRemove();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOperationEditorSave = () => {
    onUpdated?.();
  };

  const handleRunTimeFormatChange = (value: string) => {
    const parsed = RunTimeFormatSchema.safeParse(value);
    if (!parsed.success) {
      return;
    }
    const convertedRunTime = convertRunTimeValue(
      runTimePerItem,
      runTimeFormat,
      parsed.data,
      quantityPer,
    );
    setRunTimeFormat(parsed.data);
    if (convertedRunTime !== null) {
      setRunTimePerItem(convertedRunTime);
    }
  };

  const isPerUnit =
    selectedOperation?.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT;
  const defaultCost = selectedOperation
    ? isPerUnit
      ? selectedOperation.cost
      : new Decimal(selectedOperation.cost)
          .plus(selectedOperation.burdenRate ?? 0)
          .toString()
    : "";
  const handleReset = useCallback(() => {
    setOverrideOperationCost("");
    setOverrideSetupRate("");
    setMinimumCharge("");
    setShowCostOverrides(false);
    setQuantityBreaks([]);
  }, []);

  const handleAddQuantityBreak = useCallback(() => {
    setQuantityBreaks((prev) => [
      ...prev,
      { minQty: 0, overrideOperationCost: "" },
    ]);
  }, []);

  const handleUpdateQuantityBreak = useCallback(
    (index: number, field: keyof OperationQuantityBreak, value: string) => {
      setQuantityBreaks((prev) =>
        prev.map((qb, i) => {
          if (i !== index) {
            return qb;
          }
          if (field === "minQty") {
            return { ...qb, minQty: parseInt(value, 10) || 0 };
          }
          return { ...qb, overrideOperationCost: value };
        }),
      );
    },
    [],
  );

  const handleRemoveQuantityBreak = useCallback((index: number) => {
    setQuantityBreaks((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const title = isEditMode ? "Edit Operation" : "Add Operation";
  const description = locked
    ? `${lockReason ?? "These values are read-only"}. The operation record itself is still editable.`
    : isEditMode
      ? "Modify the operation details or remove it from the BOM"
      : "Select an operation and configure its timing parameters";
  const saveButtonText = isEditMode ? "Save Changes" : "Add Operation";

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(newOpen) => !isSubmitting && onOpenChange(newOpen)}
      >
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 py-4">
            <fieldset disabled={locked} className="contents">
              {sequenceNumbersEnabled && (
                <div className="grid gap-2">
                  <Label htmlFor="sequence-number">
                    Sequence Number (Optional)
                  </Label>
                  <Input
                    id="sequence-number"
                    value={sequenceNumber}
                    onChange={(event) => setSequenceNumber(event.target.value)}
                    placeholder="10"
                    maxLength={SEQUENCE_NUMBER_MAX_LENGTH}
                    disabled={isSubmitting}
                    className="w-[160px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Identifies this operation in the routing (e.g. 10, 20, 30).
                    Must be unique within this BOM.
                  </p>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="operation">Operation</Label>
                <OperationSelector
                  currentlySelectedId={
                    selectedOperationId ? selectedOperationId : null
                  }
                  onSelectId={(id) => setSelectedOperationId(id || "")}
                  placeholder="Select an operation"
                  disabled={isSubmitting}
                />
              </div>

              {selectedOperation && !isPerUnit && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="setup" className="whitespace-nowrap">
                      Setup Time (hrs)
                    </Label>
                    <Input
                      id="setup"
                      type="number"
                      step="0.01"
                      value={setupTime}
                      onChange={(e) => setSetupTime(e.target.value)}
                      placeholder="0.00"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="runtime" className="whitespace-nowrap">
                      Runtime
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="runtime"
                        type="number"
                        step="any"
                        min="0"
                        value={runTimePerItem}
                        onChange={(e) => setRunTimePerItem(e.target.value)}
                        placeholder="0.00"
                        disabled={isSubmitting}
                      />
                      {epicorPrimitiveSupportEnabled ? (
                        <Select
                          value={runTimeFormat}
                          onValueChange={handleRunTimeFormatChange}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger className="w-[170px] shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PIECES_PER_HOUR">
                              Pieces / hour
                            </SelectItem>
                            <SelectItem value="HOURS_PER_PIECE">
                              Hours / piece
                            </SelectItem>
                            <SelectItem value="MINUTES_PER_PIECE">
                              Minutes / piece
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="w-[170px] shrink-0 self-center text-sm text-muted-foreground">
                          {RUN_TIME_FORMAT_LABEL[runTimeFormat]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedOperation && !isPerUnit && efficiencyAllowed && (
                <div className="grid gap-2">
                  <Label htmlFor="efficiency">Efficiency (%)</Label>
                  <Input
                    id="efficiency"
                    type="number"
                    step="1"
                    min="1"
                    value={efficiency}
                    onChange={(e) => setEfficiency(e.target.value)}
                    placeholder="100"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {epicorPrimitiveSupportEnabled &&
                qtyPerAllowed &&
                selectedOperation && (
                  <div className="grid gap-2">
                    <Label htmlFor="quantity-per">
                      Operation Quantity Per Parent Item
                    </Label>
                    <Input
                      id="quantity-per"
                      type="number"
                      step="any"
                      min="0"
                      value={quantityPer}
                      onChange={(event) => setQuantityPer(event.target.value)}
                      placeholder="1"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      Quantity measured by this operation for one parent item.
                      For example, one four-cavity mold for four parts is 0.25
                      molds per parent part.
                    </p>
                  </div>
                )}

              {selectedOperationId && selectedOperation && (
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="cost">
                      {isPerUnit ? "Unit Cost" : "Total Production Rate"}
                    </Label>
                    {showCostOverrides ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleReset}
                        disabled={isSubmitting}
                        className="h-6 px-2 text-xs text-muted-foreground"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Reset to default
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCostOverrides(true)}
                        disabled={isSubmitting}
                        className="h-6 px-2 text-xs text-muted-foreground"
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        {isPerUnit ? "Override cost" : "Override rate"}
                      </Button>
                    )}
                  </div>
                  {showCostOverrides ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        id="cost"
                        type="number"
                        step="0.01"
                        value={overrideOperationCost}
                        onChange={(e) =>
                          setOverrideOperationCost(e.target.value)
                        }
                        placeholder={defaultCost}
                        disabled={isSubmitting}
                        className="pl-7"
                      />
                    </div>
                  ) : (
                    <p className="text-sm tabular-nums">
                      ${defaultCost}
                      {!isPerUnit && "/hr"}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {isPerUnit
                      ? "From operation default"
                      : `Labor $${selectedOperation.cost}/hr + burden $${selectedOperation.burdenRate ?? "0"}/hr`}
                  </p>
                </div>
              )}

              {showCostOverrides && selectedOperation && !isPerUnit && (
                <div className="grid gap-2">
                  <Label htmlFor="setup-rate">Override setup rate ($/hr)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="setup-rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={overrideSetupRate}
                      onChange={(e) => setOverrideSetupRate(e.target.value)}
                      placeholder={
                        selectedOperation.setupRate ??
                        (overrideOperationCost || defaultCost)
                      }
                      disabled={isSubmitting}
                      className="pl-7"
                    />
                  </div>
                </div>
              )}

              {showCostOverrides && selectedOperation && isPerUnit && (
                <>
                  {quantityBreaksAllowed && (
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label>Quantity Breaks</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAddQuantityBreak}
                          disabled={isSubmitting}
                          className="h-7 px-2"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add
                        </Button>
                      </div>
                      {quantityBreaks.length > 0 && (
                        <div className="space-y-2">
                          {quantityBreaks.map((qb, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div className="grid gap-1 flex-1">
                                <Input
                                  type="number"
                                  step="1"
                                  min="1"
                                  value={qb.minQty || ""}
                                  onChange={(e) =>
                                    handleUpdateQuantityBreak(
                                      index,
                                      "minQty",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Min qty"
                                  disabled={isSubmitting}
                                />
                              </div>
                              <div className="grid gap-1 flex-1">
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={qb.overrideOperationCost}
                                    onChange={(e) =>
                                      handleUpdateQuantityBreak(
                                        index,
                                        "overrideOperationCost",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Cost"
                                    disabled={isSubmitting}
                                    className="pl-7"
                                  />
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveQuantityBreak(index)}
                                disabled={isSubmitting}
                                className="h-9 w-9 p-0 shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {quantityBreaks.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          Add quantity-dependent cost overrides
                        </p>
                      )}
                    </div>
                  )}
                  <div className="grid gap-2">
                    <Label htmlFor="minimumCharge">
                      Minimum Charge (Optional)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        id="minimumCharge"
                        type="number"
                        step="0.01"
                        value={minimumCharge}
                        onChange={(e) => setMinimumCharge(e.target.value)}
                        placeholder="0.00"
                        disabled={isSubmitting}
                        className="pl-7"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Applied when total cost is below this amount
                    </p>
                  </div>
                </>
              )}

              <div className="grid gap-2 pt-4 border-t">
                <Label htmlFor="reasoning">Notes (Optional)</Label>
                <Textarea
                  id="reasoning"
                  value={reasoning}
                  onChange={(e) => setReasoning(e.target.value)}
                  placeholder="Any additional notes..."
                  disabled={isSubmitting}
                />
              </div>
            </fieldset>

            {isEditMode && selectedOperation && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsOperationEditorOpen(true)}
                  disabled={isSubmitting}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit {selectedOperation.name} Operation
                </Button>
              </div>
            )}
          </div>

          <SheetFooter className="flex justify-between">
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground w-full">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </div>
            ) : (
              <>
                {isEditMode && onRemove && !locked ? (
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Operation
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    {locked ? "Close" : "Cancel"}
                  </Button>
                  {!locked && (
                    <Button onClick={handleSave} disabled={isSubmitting}>
                      {saveButtonText}
                    </Button>
                  )}
                </div>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isEditMode && selectedOperationId && (
        <OperationEditorSheet
          open={isOperationEditorOpen}
          onOpenChange={setIsOperationEditorOpen}
          operationId={selectedOperationId}
          onSave={handleOperationEditorSave}
        />
      )}
    </>
  );
}

/**
 * Whether a BOM operation carries any explicitly set cost override.
 * @param operation - The saved BOM operation
 * @returns True when a rate, setup rate, quantity break, or minimum charge is set
 */
function hasCostOverride(operation: ItemOperation): boolean {
  return (
    !!operation.overrideOperationCost ||
    !!operation.overrideSetupRate ||
    (operation.quantityBreaks?.length ?? 0) > 0 ||
    !!operation.minimumCharge
  );
}
